/**
 * Layout content revision policy — single source of truth.
 *
 * To know which layout changes are revisioned, read this file. The store
 * captures a snapshot of the last successfully-persisted layout content,
 * compares it to the current state at flush time via
 * `isRevisionedLayoutChange`, and sends the result as the request's
 * top-level `revisioned` flag.
 *
 * Semantics are binary (matches op-policy.ts):
 *
 *   revisioned: true  → IDB, DDB, broadcast, bump layoutRevision
 *   revisioned: false → IDB, DDB, no broadcast, no bump
 *
 * Per-field policy (encoded in FIELD_REVISION_RULES below):
 *
 *   mosaicTree     → revisioned only when node identity changes (panels
 *                    added, removed, swapped, restructured). Split resizes
 *                    are NOT revisioned. Compared via mosaicNodesEqual.
 *   windowConfigs  → never revisioned (last-writer-wins).
 *   panels         → never revisioned (last-writer-wins).
 *   globalSettings → only keys listed in REVISIONED_GLOBAL_KEYS are
 *                    revisioned. None today.
 *
 * Mirrors the old layoutmdw `REVISION_RULES.checkCurrentNode` behavior.
 *
 * To add a new top-level field to layout content:
 *   1. Add it to LayoutSnapshot below.
 *   2. Add a rule to FIELD_REVISION_RULES — TypeScript will fail until you do.
 */

import type { GridCell, LayoutMode, MosaicNode, TabGroup } from '@/types/schemas/workspace'
import type { WindowConfig, PanelState } from './types'

// ─── Snapshot shape ─────────────────────────────────────────────────────

export interface LayoutSnapshot {
  mosaicTree: MosaicNode | null
  windowConfigs: Record<string, WindowConfig>
  panels: Record<string, PanelState>
  globalSettings: Record<string, unknown>
  tabGroups: Record<string, TabGroup>
  layoutMode: LayoutMode
  gridLayout: GridCell[]
}

/**
 * Compare grid arrangements by cell MEMBERSHIP (which panels have cells), not
 * position. Mirrors the mosaic rule: adding/removing a panel is structural
 * (revisioned, broadcast); dragging/resizing is last-writer-wins, like
 * splitPercentage.
 */
export function gridMembershipEqual(a: GridCell[], b: GridCell[]): boolean {
  if (a.length !== b.length) return false
  const ids = new Set(a.map((c) => c.i))
  return b.every((c) => ids.has(c.i))
}

/**
 * Compare tab-group membership/order across two snapshots, ignoring
 * `activeTabId` (which tab is showing is last-writer-wins view state, like
 * symbolIndex — not worth a cross-tab broadcast). A change in the set, order,
 * or host panels of the groups IS structural and should be revisioned.
 */
export function tabGroupsStructurallyEqual(
  a: Record<string, TabGroup>,
  b: Record<string, TabGroup>,
): boolean {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  for (const key of aKeys) {
    const ag = a[key]
    const bg = b[key]
    if (!bg) return false
    if (ag.tabs.length !== bg.tabs.length) return false
    if (ag.tabs.some((id, i) => id !== bg.tabs[i])) return false
  }
  return true
}

// ─── Helpers ────────────────────────────────────────────────────────────

/**
 * Compare two mosaic trees structurally, ignoring `splitPercentage`. Returns
 * true if the trees describe the same panels arranged the same way.
 */
export function mosaicNodesEqual(a: MosaicNode | null, b: MosaicNode | null): boolean {
  if (a === b) return true
  if (a === null || b === null) return false
  if (typeof a === 'string' || typeof b === 'string') return a === b
  return (
    a.direction === b.direction &&
    mosaicNodesEqual(a.first, b.first) &&
    mosaicNodesEqual(a.second, b.second)
  )
}

/**
 * Specific globalSettings keys whose changes are revisioned. Empty today.
 * Add a key here to opt it into cross-tab propagation.
 */
export const REVISIONED_GLOBAL_KEYS: readonly string[] = []

// ─── Per-field revision rules ─────────────────────────────────────────

/**
 * For each top-level layout field, decide whether the change between two
 * snapshots is revisioned. Adding a field to LayoutSnapshot forces a row
 * here (TypeScript catches it via the satisfies clause below).
 */
export const FIELD_REVISION_RULES = {
  mosaicTree: (prev, next) => !mosaicNodesEqual(prev, next),

  // last-writer-wins, never revisioned
  windowConfigs: () => false,
  panels: () => false,

  globalSettings: (prev, next) => REVISIONED_GLOBAL_KEYS.some((key) => prev[key] !== next[key]),

  // Tab membership/order is structural → revisioned. activeTabId alone is not.
  tabGroups: (prev, next) => !tabGroupsStructurallyEqual(prev, next),

  // Mode flips (convert tiled ⇄ scroll) are structural. CRITICAL for data
  // safety: without a revision bump, a stale server doc in the OTHER mode
  // compares as "same or newer" and a wake/bootstrap refetch resets the layout.
  layoutMode: (prev, next) => prev !== next,

  // Grid cell membership is structural (panel added/removed in scroll mode);
  // pure drags/resizes are last-writer-wins, like splitPercentage.
  gridLayout: (prev, next) => !gridMembershipEqual(prev, next),
} as const satisfies {
  [K in keyof LayoutSnapshot]: (prev: LayoutSnapshot[K], next: LayoutSnapshot[K]) => boolean
}

// ─── Decision function ────────────────────────────────────────────────

/**
 * Apply FIELD_REVISION_RULES across all fields. Returns true if any field's
 * change is revisioned. The first save with no previous baseline is treated
 * as revisioned.
 */
export function isRevisionedLayoutChange(
  prev: LayoutSnapshot | null,
  next: LayoutSnapshot,
): boolean {
  if (!prev) return true

  for (const key of Object.keys(FIELD_REVISION_RULES) as (keyof LayoutSnapshot)[]) {
    const rule = FIELD_REVISION_RULES[key] as (a: unknown, b: unknown) => boolean
    if (rule(prev[key], next[key])) return true
  }

  return false
}
